import * as React from "react";
import * as Icons from "@skbkontur/icons";
import { Button, Hint } from "react-ui";

import { Icon } from "../../Icons/Icon";
import { REMOVE_ROW_BUTTON_CLICK } from "../../metricsAction";
import { useGetUnitId } from "../../FormParts/Unit/UnitIdContext";
import { UnitId, unitIdToLegacyUnitId } from "../../FormParts/Unit/UnitId/UnitId";
import { useClosestParentInstanceStrict } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { AbsoluteResolvedModelPath, SimplePathToken } from "../../../../../Common/ModelPath/ModelPath";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { DomElementHack } from "../../Commons/DOM/DomElementHack";
import { useCandyTheme } from "../../Internals/ThemeProvider/CandyTheme/UseCandyTheme";

import { RemoveIcon } from "./RemoveIcon/RemoveIcon";

export interface RemoveRowButtonProps {
    onRemoveInstance: (bindingPath: AbsoluteResolvedModelPath, instance: SimplePathToken) => void;
    disabled?: boolean;
    hint?: string;
    type?: "bigicon" | "link" | "button" | "smallredicon" | "icon";
    isReadOnly?: boolean;
}

export const RemoveRowButton = (props: RemoveRowButtonProps) => {
    if (props.isReadOnly) {
        return <></>;
    }
    return <InteractiveButton {...props} />;
};

const InteractiveButton = (props: RemoveRowButtonProps) => {
    const type = props.type ?? "bigicon";
    const parentInstance = useClosestParentInstanceStrict();
    const getUnitId = useGetUnitId();
    const track = useMetrics();
    const theme = useCandyTheme();

    const pageObjectId = React.useMemo(
        () => parentInstance.bindingPath.joinWith(parentInstance.instance).toLegacyPath(),
        [parentInstance]
    );

    const handleRemoveInstance = React.useCallback(() => {
        if (props.disabled) {
            return;
        }
        props.onRemoveInstance(parentInstance.bindingPath, parentInstance.instance);
        const unitId: UnitId = getUnitId();
        track(REMOVE_ROW_BUTTON_CLICK, {
            path: parentInstance.bindingPath,
            instance: parentInstance.instance,
            page: unitIdToLegacyUnitId(unitId),
        });
    }, [parentInstance, props.disabled]);

    const testPageObject = React.useMemo(
        () => ({
            getType: () => "RemoveRowButton",
            remove: handleRemoveInstance,
        }),
        [handleRemoveInstance]
    );

    let result;
    if (type === "bigicon") {
        result = <RemoveIcon disabled={props.disabled} onClick={handleRemoveInstance} />;
    } else if (type === "smallredicon") {
        result = (
            <Button
                icon={<Icons.XIcon16Regular color={theme.semanticColors.danger.iconColor} />}
                use="link"
                disabled={props.disabled}
                onClick={handleRemoveInstance}
            />
        );
    } else if (type === "icon") {
        result = (
            <Button
                icon={<Icon name="XIcon" size={16} />}
                use="text"
                disabled={props.disabled}
                onClick={handleRemoveInstance}
            />
        );
    } else {
        result = (
            <Button
                icon={<Icons.XIcon16Regular />}
                use={type == "link" ? "link" : "default"}
                disabled={props.disabled}
                onClick={handleRemoveInstance}>
                {props.hint ?? "Удалить"}
            </Button>
        );
    }

    if (type !== "link" && type !== "button") {
        if (props.hint != undefined) {
            result = (
                <Hint pos="top" text={props.hint}>
                    <DomElementHack>{result}</DomElementHack>
                </Hint>
            );
        }
    }

    return (
        <TestPageObjectHandle id={pageObjectId} pageObject={testPageObject}>
            {result}
        </TestPageObjectHandle>
    );
};
